import {Pipe, PipeTransform} from '@angular/core';

/**
 * @pipe CurrencyFormatPipe
 * @name currencyFormat
 * @implements {PipeTransform}
 *
 * @description
 * The `CurrencyFormatPipe` is a custom Angular pipe designed to format numeric values into a specific currency-like format
 * suitable for the Indian locale ('en-IN'). It handles parsing, rounding, and locale-specific grouping.
 *
 * ## Key Responsibilities:
 * 1. **Input Parsing**: Converts input (string or number) into a floating-point number.
 * 2. **Rounding**: Rounds the number to 2 decimal places using `toFixed(2)`.
 * 3. **Locale Formatting**: Formats the resulting number using the 'en-IN' locale to apply Indian numbering system grouping (e.g., commas).
 * 4. **Fallback Handling**: Returns `0` if the input value is null, undefined, or falsy.
 *
 * ## Features:
 * - **Indian Locale Support**: Specifically targets 'en-IN' for formatting, which is useful for applications targeting Indian users.
 * - **Safe Parsing**: Uses `Number.parseFloat` to handle string inputs safely.
 * - **Zero Handling**: Explicitly returns `0` (number) when input is missing, rather than an empty string or null.
 *
 * ## Usage:
 * Use this pipe in templates to format numbers.
 *
 * @example
 * {{ 123456.789 | currencyFormat }}
 * // Output might be "1,23,456.79" (depending on browser locale implementation)
 */
@Pipe({
  name: 'currencyFormat'
})
export class CurrencyFormatPipe implements PipeTransform {

  /**
   * @method transform
   * @description
   * Transforms a number or string into a formatted currency string for the Indian locale.
   *
   * ## Logic Flow:
   * 1. Checks if `value` is truthy.
   * 2. If truthy:
   *    - Parses `value` to a float.
   *    - Calls `toFixed(2)` to round/pad to 2 decimal places (returns a string).
   *    - Converts that string back to a `Number`.
   *    - Calls `toLocaleString('en-IN')` on that number.
   * 3. If falsy:
   *    - Returns `0`.
   *
   * @param {any} value - The value to be formatted. Can be a number or a string representing a number.
   * @returns {any} - The formatted string (e.g., "1,23,456.78") or `0` if input is invalid.
   * @side-effect None.
   */
  transform(value: any): any {


    if (value) {
      value = Number.parseFloat(value).toFixed(2);
    }
    return value ? Number(value).toLocaleString('en-IN') : 0;
    // return value;
  }

}
